/*
 * Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

const RECAPTCHA_SITE_KEY =
    window.env && window.env.RECAPTCHA_SITE_KEY
        ? window.env.RECAPTCHA_SITE_KEY
        : '6LcgKqIkAAAAAIy7RtCvhNMdMxDtkIy1Pck82qik';

export const VALIDATE_USER_ACTION_STATUS_CODE = 420;

export enum RecaptchaNotificationTypes {
    LOGIN_FORBIDDEN = 'reCAPTCHA_forbidden',
}

export interface RecaptchaNotificationData {
    ttl: number;
    failedAttempts: number;
}

export enum ProtectedActionType {
    LOGIN_INSPECTOR = 'LOGIN_INSPECTOR',
    GET_DOCUMENT_INSPECTOR = 'GET_DOCUMENT_INSPECTOR',
    GET_DOCUMENT_MONITORING = 'GET_DOCUMENT_MONITORING',
}

export enum RecaptchaActivationStatus {
    WaitForActivation = 'WAIT_FOR_ACTIVATION',
    Active = 'ACTIVE',
    AnotherInitiator = 'ANOTHER_INITIATOR',
}

export default class Recaptcha {
    private siteKey: string;
    private action: ProtectedActionType;
    private activation: RecaptchaActivationStatus;
    private notifyOnActivation: () => void;

    constructor(
        action: ProtectedActionType,
        notifyOnActivation?: () => void,
        getTokenOnLoad?: (token: string) => void,
    ) {
        this.siteKey = RECAPTCHA_SITE_KEY;
        this.action = action;
        this.notifyOnActivation = notifyOnActivation;
        const isLoadingInitiated = loadReCaptcha(
            this.siteKey,
            this.action,
            this.handleActivated.bind(this),
            getTokenOnLoad,
        );

        if (isLoadingInitiated) this.activation = RecaptchaActivationStatus.WaitForActivation;
        else if (this.activation !== RecaptchaActivationStatus.WaitForActivation)
            this.activation = RecaptchaActivationStatus.AnotherInitiator;
    }

    public async getToken(action?: string): Promise<string> {
        return window.grecaptcha.enterprise.execute(this.siteKey, { action: action ? action : this.action });
    }

    public getAction(): string {
        return this.action;
    }

    public getActivationStatus(): RecaptchaActivationStatus {
        return this.activation;
    }

    private handleActivated(): void {
        this.activation = RecaptchaActivationStatus.Active;
        if (this.notifyOnActivation) this.notifyOnActivation();

        const badge = document.getElementsByClassName('grecaptcha-badge').item(0) as HTMLElement | null;
        if (badge != null) {
            badge.style.visibility = 'hidden';
        }
    }
}

const loadReCaptcha = (
    siteKey: string,
    action: ProtectedActionType,
    activatedCallback: () => void,
    getTokenCallback?: (token: string) => void,
): boolean => {
    if (window.grecaptcha !== undefined) {
        return false;
    }

    const script = document.createElement('script');
    script.src = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => {
        window.grecaptcha.enterprise.ready(() => {
            activatedCallback();
            if (getTokenCallback) {
                window.grecaptcha.enterprise.execute(siteKey, { action }).then((token: string) => {
                    getTokenCallback(token);
                });
            }
        });
    };
    script.onerror = () => {
        console.error('Error occurred while loading recaptcha script');
    };
    document.body.appendChild(script);
    return true;
};
